import React, { useEffect } from 'react';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import * as PropTypes from 'prop-types';
import {
  Button, Table, Typography, Tag,
} from 'antd';
import { FormattedMessage } from 'react-intl';
import ConfigHeading from '../components/ConfigHeading';
import { filterPublished } from './config-util';
import { selectChecklistCategoriesRequest } from '../../../../state/ui/configuration/inspection/checklistCategoryUiReducer';
import { selectChecklistCategories } from '../../../../state/domain/checklistCategories/checklistCategoriesReducer';
import {
  editChecklistCategoryNew, requestChecklistCategories,
} from '../../../../state/ui/configuration/inspection/checklistCategoryUiActions';
import EditChecklistCategory from './EditChecklistCategory';
import routeLocations from '../../../../utility/route-locations';
import { userIsAdmin } from '../company/user-util';
import { selectSessionUser } from '../../../../state/ui/application/sessionUiReducer';

const { Column } = Table;
const { Text } = Typography;

// SORTS
function sortTypesByDesc(a, b) {
  const aStr = `${a.categoryDescription}`;
  const bStr = `${b.categoryDescription}`;
  return aStr.localeCompare(bStr);
}

function ChecklistCategoriesTable({
  loading, checklistCategories, onEditButton, sessionUser,
}) {
  const RowTotal = () => (
    <div>
      <Text>{`${checklistCategories.length} `}</Text>
      <FormattedMessage id="items" />
    </div>
  );

  // TABLE SETTINGS, COLUMNS
  return (
    <Table
      rowKey={checklistCategory => checklistCategory.categoryCode}
      loading={loading}
      pagination={{ defaultPageSize: 10, showTotal: () => <RowTotal /> }}
      scroll={{ x: 1, y: false }}
      dataSource={checklistCategories}
    >
      <Column
        title={<FormattedMessage id="published" />}
        dataIndex="active"
        key="active"
        align="center"
        filtered
        filters={[{ text: <FormattedMessage id="published" />, value: true }, { text: <FormattedMessage id="notPublished" />, value: false }]}
        filteredValue={checklistCategories.active}
        onFilter={filterPublished}
        width={110}
        render={(active, type) => (
          <Tag color={type.active ? 'blue' : 'volcano'}>
            {(type.active ? <FormattedMessage id="published" /> : <FormattedMessage id="notPublished" />)}
          </Tag>
        )}
      />
      <Column
        defaultSortOrder="ascend"
        sorter={sortTypesByDesc}
        title={<FormattedMessage id="description" />}
        width="75%"
        dataIndex="categoryDescription"
        key="categoryDescription"
      />
      { userIsAdmin(sessionUser)
        ? (
          <Column
            className="via-action-column"
            align="center"
            width={70}
            render={(text, record) => (
              <Button size="small" type="primary" onClick={() => onEditButton(record)} icon="edit">
                <FormattedMessage id="edit" />
              </Button>
            )}
          />
        ) : ''
      }
    </Table>
  );
}

ChecklistCategoriesTable.propTypes = {
  loading: PropTypes.bool.isRequired,
  onEditButton: PropTypes.func.isRequired,
  checklistCategories: PropTypes.arrayOf(
    PropTypes.shape({
      categoryCode: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      categoryDescription: PropTypes.string.isRequired,
    }).isRequired,
  ),
  sessionUser: PropTypes.shape({
    linkedRoles: PropTypes.arrayOf(PropTypes.string).isRequired,
  }).isRequired,
};

ChecklistCategoriesTable.defaultProps = {
  checklistCategories: [],
};

function ChecklistCategories({
  isLoading, checklistCategories, allChecklistCategories, editChecklistCategory, loadChecklistCategories, sessionUser,
}) {
  // Load Checklist Categories on first entry.
  // These only need to be loaded once.
  useEffect(() => {
    loadChecklistCategories();
  }, [loadChecklistCategories]);

  // Extra validation helper
  const doesDescriptionExist = (code, value) => {
    const matchedCategory = allChecklistCategories.find(row => row.categoryDescription.toString().toLowerCase() === (value || '').toString().toLowerCase());
    return matchedCategory ? matchedCategory.categoryCode !== code : false;
  };

  // TITLE ROW
  const CreateButton = () => (
    <Button type="primary" onClick={() => editChecklistCategory(null)} loading={isLoading} icon="plus">
      <FormattedMessage id="create" />
    </Button>
  );

  // MAIN COMPONENT
  return (
    <ConfigHeading
      titleId="checklistCategories"
      descriptionId="checklistCategoriesDesc"
      actions={(
        userIsAdmin(sessionUser)
          ? <CreateButton />
          : <></>
      )}
      links={[(
        <Link to={routeLocations.configure_checklists}>
          <FormattedMessage id="checklists" />
        </Link>
      )]}
    >
      <ChecklistCategoriesTable
        loading={isLoading}
        checklistCategories={checklistCategories}
        onEditButton={editChecklistCategory}
        sessionUser={sessionUser}
      />
      <EditChecklistCategory descValidator={doesDescriptionExist} />
    </ConfigHeading>
  );
}

/**
 * Validation for the props on the component.
 */
ChecklistCategories.propTypes = {
  isLoading: PropTypes.bool,
  loadChecklistCategories: PropTypes.func.isRequired,
  editChecklistCategory: PropTypes.func.isRequired,
  checklistCategories: PropTypes.arrayOf(
    PropTypes.shape({
      categoryCode: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      categoryDescription: PropTypes.string.isRequired,
    }),
  ),
  allChecklistCategories: PropTypes.arrayOf(
    PropTypes.shape({
      categoryCode: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      categoryDescription: PropTypes.string.isRequired,
    }),
  ),
  sessionUser: PropTypes.shape({
    linkedRoles: PropTypes.arrayOf(PropTypes.string).isRequired,
  }).isRequired,
};

/**
 * Default data for the component if nothing has been supplied.
 * Only needed for non-required props.
 */
ChecklistCategories.defaultProps = {
  isLoading: false,
  checklistCategories: null,
  allChecklistCategories: null,
};

/**
 * A function for connecting redux state to the component props.
 */
const mapStateToProps = (state) => {
  const checklistCategoriesRequest = selectChecklistCategoriesRequest(state);
  const checklistCategories = selectChecklistCategories(state);

  return {
    isLoading: checklistCategoriesRequest ? checklistCategoriesRequest.isLoading : false,
    error: checklistCategoriesRequest ? checklistCategoriesRequest.error : null,
    checklistCategories,
    allChecklistCategories: checklistCategories || [],
    sessionUser: selectSessionUser(state),
  };
};

/**
 * A function for connecting redux action to the component props.
 */
const mapDispatchToProps = dispatch => ({
  loadChecklistCategories: () => dispatch(requestChecklistCategories()),
  editChecklistCategory: checklistCategory => dispatch(editChecklistCategoryNew(checklistCategory ? checklistCategory.categoryCode : null)),
});

/**
 * Connect the redux state to our component.
 */
export default connect(mapStateToProps, mapDispatchToProps)(ChecklistCategories);
